import { ComplianceFrameworks } from '@/api/rest/resources/types/organization';
import { PlotStatusEnum } from '@/api/rest/resources/types/plot';
import { ProjectStatus } from '@/api/rest/resources/types/project';

export const APP_VERSION = import.meta.env.APP_VERSION as string; // passed from Docker
export const REST_API_URL = import.meta.env.REST_API_URL as string;
export const SHOW_APPROXIMATE_PLOTS_AS_PINS = (import.meta.env.SHOW_APPROXIMATE_PLOTS_AS_PINS as string) !== 'false';
export const FEATURE_FLAG_ENABLE_AUTO_PLOT_DETECTION =
  (import.meta.env.FEATURE_FLAG_ENABLE_AUTO_PLOT_DETECTION as string) === 'true';
export const FRONTEND_MAX_UPLOAD_FILE_SIZE_MB = parseInt(
  import.meta.env.FRONTEND_MAX_UPLOAD_FILE_SIZE_MB as string,
  10,
);

export const REST_API_VERSION = 'v1';
export const MAPBOX_TOKEN =
  'pk.eyJ1Ijoicm9ubnktcmVudG5lciIsImEiOiJjbDRqZHhscTUwbDU4M2J1YnpnOWwxYTJ5In0.wIjndwWC0pPZ6YDX8qzK7Q';
export const MAPBOX_NO_LABELS = {
  token: 'pk.eyJ1IjoiZ2FyZXRoLXByaWVkZSIsImEiOiJjbG1yYnEwejIwNWxkMmtxbWR6ZHp2YXN5In0.tzkWc325BHZefvU1hrukmw',
  ownerId: 'gareth-priede',
  styleId: 'clmu660l102fj01qu29f822xb',
};

export const DEFAULT_MAP_SEARCH_ZOOM = 16;
export const DEFAULT_MAP_LOCATION = {
  longitude: 11.6898361,
  latitude: 49.6889203,
  zoom: 2,
};
export const LIGHT_MAP_STYLE = 'mapbox://styles/mapbox/light-v10';
export const DEFAULT_MAP_STYLE = 'mapbox://styles/mapbox/satellite-streets-v12';
export const COOKIE_IDENTIFIER_OF_COOKIE_BANNER = 'cookie_consent';
export const DEFAULT_DELAY_TIMER = 1000;
export const LANDING_PAGE_URL = 'https://www.thelandbankinggroup.com';
export const METHODOLOGY_EXPLAINER_PAGE_BU = 'https://thelandbankinggroup.notion.site';
export const METHODOLOGY_EXPLAINER_PAGE_VNU = 'https://www.africanparks.org/sites/default/files/More%20on%20VNU.pdf';
export const METHODOLOGY_EXPLAINER_PAGE_NCI = 'https://science.landler.io/natural-capital-indicators';
export const METHODOLOGY_EXPLAINER_PAGE_WATER_INDICATORS =
  'https://science.landler.io/natural-capital-indicators/water-indicators';
export const METHODOLOGY_EXPLAINER_PAGE_CARBON_INDICATORS =
  'https://science.landler.io/natural-capital-indicators/carbon-indicators';
export const METHODOLOGY_EXPLAINER_PAGE_BIODIVERSITY_INDICATORS =
  'https://science.landler.io/natural-capital-indicators/biodiversity-indicators';
export const METHODOLOGY_EXPLAINER_PAGE_NDVI =
  'https://www.thelandbankinggroup.com/science/indicators/internal-vegetation-condition';
export const METHODOLOGY_EXPLAINER_PAGE_HABITAT_INTACTNESS =
  'https://www.thelandbankinggroup.com/science/indicators/habitat-intactness';
export const METHODOLOGY_EXPLAINER_PAGE_EII = 'https://ecosystemintegrityindex.org/';
export const SENTRY_DSN = import.meta.env.SENTRY_DSN as string;
export const METHODOLOGY_VERSIONS = {
  soilGrids: '2.0',
  worldCover: '2.0',
} as const;

// Scientific conversion factors

export const CARBON_TONNES_TO_CO2_MULTIPLIER = 3.67;

export const METHODOLOGY_LANDLER_RULEBOOK = {
  ECOLOGICAL_INTEGRITY:
    'https://rulebook.landler.io/section-b-the-measurement-of-land-characteristics/dimension-biodiversity/indicator-ecological-integrity',
  HABITAT_INTACTNESS:
    'https://rulebook.landler.io/section-b-the-measurement-of-land-characteristics/dimension-biodiversity/indicator-habitat-intactness',
  INDICATOR_SPECIES:
    'https://rulebook.landler.io/section-b-the-measurement-of-land-characteristics/dimension-biodiversity/indicator-indicator-species-presence',
} as const;
export const UNANALYZABLE_PLOT_STATUS_TYPES = [PlotStatusEnum.invalid, PlotStatusEnum.new_plot, PlotStatusEnum.draft];
export const ONGOING_ANALYSIS_PROJECT_STATUS_TYPES = [
  ProjectStatus.scheduled_for_analysis,
  ProjectStatus.analysing_project_pipeline,
];
export const UNEDITABLE_PROJECT_TYPES = [ProjectStatus.locked, ProjectStatus.invested];
export const BUYER_CALENDLY_LINK = 'https://calendly.com/sophie-vanberchem/30min';
export const LANDSTEWARD_CALENDLY_LINK = 'https://calendly.com/sophie-vanberchem/30min';
export const CONTACT_SUPPORT_EMAIL_ADDRESS = 'cs@thelandbankinggroup.com';
export const CONTACT_SUPPORT_TEAM_FORM_URL = ' https://fcdk5.share-eu1.hsforms.com/23h3FSLJXQ-Cth6GrlVRGqQ';
export const SELF_SIGNUP_ENABLED = import.meta.env.SELF_SIGNUP_ENABLED === 'true';

export type ComplianceFrameworkLogo = (keyof typeof ComplianceFrameworks)[number];

export const COMPLIANCE_LOGOS_BIODIVERSITY: ComplianceFrameworkLogo[] = ['sai', 'eudr'];
export const COMPLIANCE_LOGOS_NDVI: ComplianceFrameworkLogo[] = ['npi', 'csrd', 'sbtn', 'tnfd'];
export const COMPLIANCE_LOGOS_WATER: ComplianceFrameworkLogo[] = ['sai'];
export const COMPLIANCE_LOGOS_CARBON: ComplianceFrameworkLogo[] = ['science', 'greenhouse', 'sai'];
export const COMPLIANCE_LOGOS_ABOVE_GROUND_CARBON: ComplianceFrameworkLogo[] = ['science', 'greenhouse', 'sai', 'tnfd'];
export const COMPLIANCE_LOGOS_LAND_COVER_CHANGE: ComplianceFrameworkLogo[] = ['tnfd', 'csrd', 'npi', 'sbtn'];
export const COMPLIANCE_LOGOS_CANOPY_COVER: ComplianceFrameworkLogo[] = ['science', 'tnfd', 'rac'];
export const COMPLIANCE_LOGOS_CANOPY_HEIGHT: ComplianceFrameworkLogo[] = ['science', 'tnfd', 'rac'];
export const COMPLIANCE_LOGOS_PROTECTED_AREAS: ComplianceFrameworkLogo[] = ['tnfd', 'csrd', 'npi', 'sbtn'];
export const COMPLIANCE_LOGOS_THREATENED_SPECIES: ComplianceFrameworkLogo[] = ['tnfd', 'csrd', 'npi', 'sbtn'];
export const COMPLIANCE_LOGOS_HABITAT_INTACTNESS: ComplianceFrameworkLogo[] = ['tnfd', 'csrd', 'npi', 'sbtn'];
export const COMPLIANCE_LOGOS_EII: ComplianceFrameworkLogo[] = ['npi', 'tnfd', 'sbtn', 'csrd'];
export const COMPLIANCE_LOGOS_DEFORESTATION: ComplianceFrameworkLogo[] = [
  'csrd',
  'eudr',
  'npi',
  'sbtn',
  'sbti',
  'tnfd',
];

export const PRIVACY_POLICY_VERSION = '2023-04';

export const PLOT_MIN_AREA_IN_M2 = import.meta.env.PLOT_MIN_AREA_IN_M2 as string;
export const PLOT_MAX_AREA_IN_M2 = import.meta.env.PLOT_MAX_AREA_IN_M2 as string;
export const MAP_MAX_ZOOM = 16;

// TODO: MVP-3284 rename to MAP_OVERVIEW_PADDING and delete the old export
export const MAP_OVERVIEW_PADDING_MOBILE = {
  left: 20,
  right: 20,
  top: 20,
  bottom: 20,
} as const;
export const MAP_OVERVIEW_PADDING = {
  left: 56, // marker pin
  right: 56, // marker pin
  top: 170, // HUD
  bottom: 64, // half of bottom decoration
} as const;

export const CONSERVATION_ORGANISATIONS = ['African Parks', 'AMES Foundation'];

export const NO_DATA_PLACEHOLDER = '-';

// SUPER HACK: to make iucn url unguessable. to be removed when keycloak is setup on geoserver
export const IUCN_STATIC_HASH = '30e363d3e8c59f2c1319f8d73d48e3ad26db5e087951a4d7ab809c6f5401aea8';
